import React, { forwardRef, useEffect } from 'react'
import { InputCharLimit, TextAreaInput } from '@lightspeed/design-system-react'
import { detectCreditCard } from '@vend/credit-card-detector'
import { HasCCErrorMessage } from './has-cc-error-message/HasCCErrorMessage'

const NOTE_THRESHOLD = 50

type Props = {
  placeholder?: string
  note: string
  hasError?: boolean
  charLimit: number
  autoFocus?: boolean
  disabled?: boolean
  rows?: number
  name?: string
  dataTestId?: string
  onInput?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  onBlur?: () => void
  onErrorsChange?: (errors: NoteError[]) => void
  onValidChange?: (valid: boolean) => void
}

export type NoteError = 'CC_DETECTED' | 'TOO_LONG'
export const CreditCardDetectedError: NoteError = 'CC_DETECTED'
export const NoteTooLongError: NoteError = 'TOO_LONG'

export const NoteInput = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      placeholder,
      note,
      hasError,
      charLimit,
      autoFocus,
      disabled,
      rows,
      name,
      dataTestId,
      onInput,
      onChange,
      onBlur,
      onErrorsChange,
      onValidChange,
    },
    ref
  ) => {
    const hasCreditCard = detectCreditCard(note)
    const noteTooLong = note.length > charLimit

    useEffect(() => {
      if (!onErrorsChange) {
        return
      }

      const errs: NoteError[] = []

      if (hasCreditCard) {
        errs.push(CreditCardDetectedError)
      }

      if (noteTooLong) {
        errs.push(NoteTooLongError)
      }

      onErrorsChange(errs)
    }, [hasCreditCard, noteTooLong, onErrorsChange])

    useEffect(() => {
      if (!onValidChange) {
        return
      }
      onValidChange(!hasCreditCard && !noteTooLong)
    }, [hasCreditCard, noteTooLong, onValidChange])

    return (
      <div className="vd-flex vd-flex--column">
        <TextAreaInput
          ref={ref}
          placeholder={placeholder}
          hasError={hasError || noteTooLong || hasCreditCard}
          value={note}
          disabled={disabled}
          rows={rows}
          autoFocus={autoFocus}
          name={name}
          data-testid={dataTestId}
          onInput={onInput}
          onChange={onChange}
          onBlur={onBlur}
        />
        <InputCharLimit
          limit={charLimit}
          threshold={NOTE_THRESHOLD}
          value={note}
        />
        {hasCreditCard && <HasCCErrorMessage />}
      </div>
    )
  }
)
